// src/pages/hotel/HotelDetails.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  MapPin, 
  Calendar, 
  Users, 
  Star, 
  Coffee, 
  Wifi, 
  Navigation, 
  ChevronLeft, 
  ChevronRight,
  Check,
  Info,
  Clock,
  Heart,
  ThumbsUp,
  MessageSquare,
  Flag,
  ChevronDown,
  ChevronUp,
  Image
} from 'lucide-react';
import hotelData from '../../data/hotels.json';
import "../../styles/hotel/hotel-details.css";

const HotelDetails = () => {
  const { id } = useParams();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showGuestPicker, setShowGuestPicker] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [expandedReview, setExpandedReview] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [fullScreenBooking, setFullScreenBooking] = useState(false);
  const [selectedPhotoCategory, setSelectedPhotoCategory] = useState('all');
  const [bookingInfo, setBookingInfo] = useState({
    checkIn: '',
    checkOut: '',
    guests: {
      adults: 2,
      children: 0,
      rooms: 1
    }
  });

  // Check for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate nights and total price
  const calculateNights = () => {
    if (!bookingInfo.checkIn || !bookingInfo.checkOut) return 0;
    const checkIn = new Date(bookingInfo.checkIn);
    const checkOut = new Date(bookingInfo.checkOut);
    return Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));
  };

  const nights = calculateNights();
  const totalPrice = hotel ? hotel.price * nights * bookingInfo.guests.rooms : 0;

  // Fetch hotel data
  useEffect(() => {
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      const foundHotel = hotelData.hotels.find(h => h.id.toString() === id);
      
      if (foundHotel) {
        // Add more details for the details page
        foundHotel.images = [
          foundHotel.image, // Main image
          "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?ixlib=rb-4.0.3",
          "https://images.unsplash.com/photo-1566665797739-1674de7a421a?ixlib=rb-4.0.3",
          "https://images.unsplash.com/photo-1590490360182-c33d57733427?ixlib=rb-4.0.3",
          "https://images.unsplash.com/photo-1584132967334-10e028bd69f7?ixlib=rb-4.0.3",
          "https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3",
          "https://images.unsplash.com/photo-1582719508461-905c673771fd?ixlib=rb-4.0.3",
          "https://images.unsplash.com/photo-1586611292717-f828b167408c?ixlib=rb-4.0.3",
          "https://images.unsplash.com/photo-1445991842772-097fea258e7b?ixlib=rb-4.0.3",
          "https://images.unsplash.com/photo-1562778612-e1e0cda9915c?ixlib=rb-4.0.3"
        ];
        
        // Add photo categories for the gallery tab
        foundHotel.photoCategories = {
          all: foundHotel.images,
          rooms: [
            "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?ixlib=rb-4.0.3",
            "https://images.unsplash.com/photo-1566665797739-1674de7a421a?ixlib=rb-4.0.3",
            "https://images.unsplash.com/photo-1586611292717-f828b167408c?ixlib=rb-4.0.3"
          ],
          bathroom: [
            "https://images.unsplash.com/photo-1584132967334-10e028bd69f7?ixlib=rb-4.0.3",
            "https://images.unsplash.com/photo-1562778612-e1e0cda9915c?ixlib=rb-4.0.3"
          ],
          dining: [
            "https://images.unsplash.com/photo-1590490360182-c33d57733427?ixlib=rb-4.0.3",
            "https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3"
          ],
          amenities: [
            "https://images.unsplash.com/photo-1582719508461-905c673771fd?ixlib=rb-4.0.3",
            "https://images.unsplash.com/photo-1445991842772-097fea258e7b?ixlib=rb-4.0.3"
          ]
        };
        
        foundHotel.description = "This luxurious hotel offers stunning views and exceptional service. Located in the heart of the city, guests enjoy easy access to popular attractions, shopping districts, and vibrant nightlife. The elegant rooms feature modern amenities and comfortable furnishings, ensuring a relaxing stay.";
        
        foundHotel.amenitiesDetails = {
          basic: ["Free Wi-Fi", "Air Conditioning", "Flat-screen TV", "Safe", "Minibar"],
          bathroom: ["Private bathroom", "Free toiletries", "Hair dryer", "Bathrobe", "Slippers"],
          food: ["Restaurant", "Room service", "Breakfast available", "Bar/Lounge", "Coffee shop"],
          services: ["24-hour front desk", "Concierge service", "Luggage storage", "Laundry service", "Airport shuttle"]
        };
        
        foundHotel.policies = {
          checkIn: "From 2:00 PM",
          checkOut: "Until 12:00 PM",
          cancellation: "Free cancellation up to 24 hours before check-in",
          children: "Children of all ages are welcome",
          pets: "Pets are not allowed",
          payment: "Accepts major credit cards"
        };
        
        foundHotel.reviews = {
          count: foundHotel.reviews?.count || Math.floor(Math.random() * 500) + 50,
          average: foundHotel.rating || (Math.random() * 2 + 3).toFixed(1),
          categories: {
            location: (Math.random() * 2 + 3).toFixed(1),
            cleanliness: (Math.random() * 2 + 3).toFixed(1),
            service: (Math.random() * 2 + 3).toFixed(1),
            value: (Math.random() * 2 + 3).toFixed(1)
          },
          items: [
            {
              id: 1,
              name: "John D.",
              avatar: "/api/placeholder/50/50",
              date: "March 2, 2025",
              rating: 5,
              comment: "Excellent location and friendly staff. The room was clean and comfortable. Would definitely stay here again! The hotel amenities were great and the staff was very accommodating with our late check-in.",
              helpful: 12,
              replies: 1,
              categories: {
                location: 5,
                cleanliness: 5,
                service: 4.5,
                value: 4
              }
            },
            {
              id: 2,
              name: "Sarah M.",
              avatar: "/api/placeholder/50/50",
              date: "February 15, 2025",
              rating: 4,
              comment: "Great hotel in a convenient location. The breakfast was delicious and had many options. Only downside was the slow Wi-Fi. Otherwise, everything was excellent - the rooms were spacious and well-maintained.",
              helpful: 8,
              replies: 2,
              categories: {
                location: 5,
                cleanliness: 4,
                service: 4,
                value: 4.5
              }
            },
            {
              id: 3,
              name: "Robert K.",
              avatar: "/api/placeholder/50/50",
              date: "January 28, 2025",
              rating: 5,
              comment: "One of the best hotels I've stayed at. The staff went above and beyond to make my stay comfortable. The amenities were excellent and the view from our room was spectacular. Would absolutely recommend to anyone visiting the area.",
              helpful: 24,
              replies: 3,
              categories: {
                location: 4.5,
                cleanliness: 5,
                service: 5,
                value: 4.5
              }
            }
          ]
        };
        
        foundHotel.rooms = [
          {
            id: 1,
            name: "Deluxe King Room",
            price: foundHotel.price,
            size: "30 m²",
            beds: "1 King Bed",
            occupancy: "2 Adults",
            image: "https://images.unsplash.com/photo-1611892440504-42a792e24d32?ixlib=rb-4.0.3",
            amenities: ["Free Wi-Fi", "Air Conditioning", "Flat-screen TV", "Private bathroom"]
          },
          {
            id: 2,
            name: "Premium Twin Room",
            price: foundHotel.price + 50,
            size: "35 m²",
            beds: "2 Queen Beds",
            occupancy: "2 Adults, 2 Children",
            image: "https://images.unsplash.com/photo-1595576508898-0ad5c879a061?ixlib=rb-4.0.3",
            amenities: ["Free Wi-Fi", "Air Conditioning", "Flat-screen TV", "Private bathroom", "City view"]
          },
          {
            id: 3,
            name: "Executive Suite",
            price: foundHotel.price + 120,
            size: "55 m²",
            beds: "1 King Bed",
            occupancy: "2 Adults, 1 Child",
            image: "https://images.unsplash.com/photo-1609949279531-cf48d64a564b?ixlib=rb-4.0.3",
            amenities: ["Free Wi-Fi", "Air Conditioning", "Flat-screen TV", "Private bathroom", "Living area", "Minibar", "City view"]
          }
        ];

        foundHotel.nearbyAttractions = [
          { name: "Central Park", distance: "0.3 km" },
          { name: "Museum of Modern Art", distance: "0.7 km" },
          { name: "Shopping District", distance: "1.2 km" },
          { name: "Beach Access", distance: "2.5 km" }
        ];
      }
      
      setHotel(foundHotel);
      setLoading(false);
    }, 1000);
  }, [id]);

  // Image gallery controls
  const nextImage = () => {
    if (!hotel || !hotel.images) return;
    setCurrentImageIndex((prev) => 
      prev === hotel.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    if (!hotel || !hotel.images) return;
    setCurrentImageIndex((prev) => 
      prev === 0 ? hotel.images.length - 1 : prev - 1
    );
  };

  // Handle guest changes
  const handleGuestChange = (type, action) => {
    setBookingInfo(prev => ({
      ...prev,
      guests: {
        ...prev.guests,
        [type]: action === 'increment' 
          ? prev.guests[type] + 1 
          : Math.max(type === 'adults' ? 1 : 0, prev.guests[type] - 1)
      }
    }));
  };

  // Toggle expanded review
  const toggleExpandReview = (id) => {
    if (expandedReview === id) {
      setExpandedReview(null);
    } else {
      setExpandedReview(id);
    }
  };

  // Render star ratings
  const renderStars = (rating) => {
    return (
      <div className="d-flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star} 
            size={18} 
            className={star <= rating ? "text-warning" : "text-secondary"} 
            fill={star <= rating ? "#ffc107" : "none"} 
          />
        ))}
      </div>
    );
  };

  // Get color for category rating
  const getCategoryColor = (score) => {
    if (score >= 4.5) return "bg-success";
    if (score >= 4) return "bg-success-lighter";
    if (score >= 3) return "bg-warning";
    return "bg-danger";
  };

  // Toggle fullscreen booking on mobile
  const toggleFullScreenBooking = () => {
    setFullScreenBooking(!fullScreenBooking);
  };

  // Close guest picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showGuestPicker && !event.target.closest('.guest-picker')) {
        setShowGuestPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGuestPicker]);

  // Loading state
  if (loading) {
    return (
      <div className="container py-5">
        <div className="row">
          <div className="col-12">
            <div className="skeleton-image"></div>
            <div className="skeleton-line w-50 mt-4"></div>
            <div className="skeleton-line w-75"></div>
            <div className="skeleton-line w-25"></div>
          </div>
        </div>
      </div>
    );
  }

  // Hotel not found
  if (!hotel) {
    return (
      <div className="container py-5 text-center">
        <Info size={48} className="text-muted mb-3" />
        <h2>Hotel Not Found</h2>
        <p className="text-muted">The hotel you're looking for doesn't exist or has been removed.</p>
        <Link to="/hotels" className="btn btn-primary mt-3">
          Back to Hotels
        </Link>
      </div>
    );
  }

  return (
    <div className="hotel-details-page">
      {/* Breadcrumbs */}
      <div className="container pt-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/">Home</Link></li>
            <li className="breadcrumb-item"><Link to="/hotels">Hotels</Link></li>
            <li className="breadcrumb-item active" aria-current="page">{hotel.name}</li>
          </ol>
        </nav>
      </div>

      {/* Hotel Header */}
      <div className="container mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h1 className="hotel-title">{hotel.name}</h1>
            <div className="d-flex align-items-center mb-2 flex-wrap">
              <div className="hotel-stars me-3">
                {[...Array(hotel.stars)].map((_, i) => (
                  <Star key={i} size={16} className="text-warning" fill="#ffc107" />
                ))}
              </div>
              <span className="badge bg-primary me-2">
                {hotel.rating} / 5
              </span>
              <span className="text-muted">
                ({hotel.reviews.count} reviews)
              </span>
            </div>
            <p className="location">
              <MapPin size={16} className="me-1" />
              {`${hotel.location.city}, ${hotel.location.country}`}
            </p>
          </div>
          <div className="mt-2 mt-sm-0">
            <button 
              className="btn btn-outline-danger favorite-btn"
              onClick={() => setIsFavorite(!isFavorite)}
            >
              <Heart size={20} fill={isFavorite ? "#dc3545" : "none"} />
              <span className="ms-2 d-none d-md-inline">
                {isFavorite ? "Saved" : "Save"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Image Gallery */}
      <div className="image-gallery-container mb-5">
        <div className="container">
          <div className="image-gallery">
            <div className="main-image-container">
              <img 
                src={hotel.images[currentImageIndex]} 
                alt={`${hotel.name} - view ${currentImageIndex + 1}`}
                className="main-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://via.placeholder.com/800x500?text=Hotel+Image";
                }}
              />
              <button 
                className="gallery-control prev"
                onClick={prevImage}
              >
                <ChevronLeft size={24} />
              </button>
              <button 
                className="gallery-control next"
                onClick={nextImage}
              >
                <ChevronRight size={24} />
              </button>
              <div className="image-counter">
                {currentImageIndex + 1} / {hotel.images.length}
              </div>
            </div>
            <div className="image-thumbnails">
              {hotel.images.slice(0, 5).map((image, index) => (
                <div 
                  key={index}
                  className={`thumbnail-wrapper ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                >
                  <img 
                    src={image} 
                    alt={`${hotel.name} thumbnail ${index + 1}`}
                    className="thumbnail"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/150x150?text=Thumbnail";
                    }}
                  />
                </div>
              ))}
              {hotel.images.length > 5 && (
                <div 
                  className="thumbnail-wrapper more-photos"
                  onClick={() => {
                    setActiveTab('photos');
                    window.scrollTo(0, document.querySelector('.details-tabs').offsetTop - 100);
                  }}
                >
                  <div className="more-photos-overlay">
                    <Image size={24} />
                    <span>+{hotel.images.length - 5}</span>
                  </div>
                  <img 
                    src={hotel.images[5]} 
                    alt={`${hotel.name} more photos`}
                    className="thumbnail"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/150x150?text=More+Photos";
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Booking Trigger */}
      {isMobile && (
        <div className="mobile-booking-trigger">
          <div className="container">
            <div className="d-flex justify-content-between align-items-center p-3 bg-white shadow-sm rounded">
              <div>
                <div className="fw-bold fs-5">${hotel.price}</div>
                <div className="small text-muted">per night</div>
              </div>
              <button 
                className="btn btn-primary"
                onClick={toggleFullScreenBooking}
              >
                Book Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="container mb-5">
        <div className="row g-4">
          {/* Main Content */}
          <div className="col-lg-8">
            {/* Tabs */}
            <div className="details-tabs mb-4">
  <div className="tabs-container">
    <ul className="nav nav-tabs flex-nowrap">
      <li className="nav-item">
        <button
          className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
      </li>
      <li className="nav-item">
        <button
          className={`nav-link ${activeTab === 'photos' ? 'active' : ''}`}
          onClick={() => setActiveTab('photos')}
        >
          Photos
        </button>
      </li>
      <li className="nav-item">
        <button
          className={`nav-link ${activeTab === 'rooms' ? 'active' : ''}`}
          onClick={() => setActiveTab('rooms')}
        >
          Rooms
        </button>
      </li>
      <li className="nav-item">
        <button
          className={`nav-link ${activeTab === 'amenities' ? 'active' : ''}`}
          onClick={() => setActiveTab('amenities')}
        >
          Amenities
        </button>
      </li>
      <li className="nav-item">
        <button
          className={`nav-link ${activeTab === 'reviews' ? 'active' : ''}`}
          onClick={() => setActiveTab('reviews')}
        >
          Reviews
        </button>
      </li>
      <li className="nav-item">
        <button
          className={`nav-link ${activeTab === 'policies' ? 'active' : ''}`}
          onClick={() => setActiveTab('policies')}
        >
          Policies
        </button>
      </li>
    </ul>
  </div>
</div>

            {/* Tab Content */}
            <div className="tab-content py-3">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="overview-tab">
                  <div className="card mb-4">
                    <div className="card-body">
                      <h2 className="card-title h4 mb-3">About this hotel</h2>
                      <p className="card-text">{hotel.description}</p>
                    </div>
                  </div>
                  
                  <div className="card mb-4">
                    <div className="card-body">
                      <h2 className="card-title h4 mb-3">Highlights</h2>
                      <div className="row">
                        <div className="col-md-6">
                          <ul className="highlights-list">
                            <li>
                              <Check size={16} className="text-success me-2" />
                              Located in {hotel.location.city} center
                            </li>
                            <li>
                              <Check size={16} className="text-success me-2" />
                              {hotel.amenities.includes("Free WiFi") ? "Free WiFi" : hotel.amenities[0]}
                            </li>
                            <li>
                              <Check size={16} className="text-success me-2" />
                              {hotel.stars}-star accommodation
                            </li>
                          </ul>
                        </div>
                        <div className="col-md-6">
                          <ul className="highlights-list">
                            <li>
                              <Check size={16} className="text-success me-2" />
                              {hotel.amenities.includes("Breakfast included") ? "Breakfast included" : hotel.amenities[1]}
                            </li>
                            <li>
                              <Check size={16} className="text-success me-2" />
                              {hotel.amenities.includes("Parking") ? "Parking available" : hotel.amenities[2]}
                            </li>
                            <li>
                              <Check size={16} className="text-success me-2" />
                              {hotel.policies?.cancellation || "Free cancellation available"}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card mb-4">
                    <div className="card-body">
                      <h2 className="card-title h4 mb-3">Nearby Attractions</h2>
                      <ul className="nearby-attractions">
                        {hotel.nearbyAttractions?.map((attraction, index) => (
                          <li key={index} className="d-flex align-items-center mb-2">
                            <Navigation size={16} className="me-2 text-primary" />
                            <span className="attraction-name">{attraction.name}</span>
                            <span className="ms-auto attraction-distance">{attraction.distance}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Photo Gallery Tab */}
              {activeTab === 'photos' && (
                <div className="photos-tab">
                  <h2 className="h4 mb-3">Hotel Photos</h2>
                  
                  {/* Photo Categories */}
                  <div className="photo-categories mb-4">
                    <div className="d-flex flex-wrap gap-2">
                      <button 
                        className={`btn ${selectedPhotoCategory === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setSelectedPhotoCategory('all')}
                      >
                        All Photos ({hotel.photoCategories.all.length})
                      </button>
                      <button 
                        className={`btn ${selectedPhotoCategory === 'rooms' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setSelectedPhotoCategory('rooms')}
                      >
                        Rooms ({hotel.photoCategories.rooms.length})
                      </button>
                      <button 
                        className={`btn ${selectedPhotoCategory === 'bathroom' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setSelectedPhotoCategory('bathroom')}
                      >
                        Bathroom ({hotel.photoCategories.bathroom.length})
                      </button>
                      <button 
                        className={`btn ${selectedPhotoCategory === 'dining' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setSelectedPhotoCategory('dining')}
                      >
                        Dining ({hotel.photoCategories.dining.length})
                      </button>
                      <button 
                        className={`btn ${selectedPhotoCategory === 'amenities' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setSelectedPhotoCategory('amenities')}
                      >
                        Amenities ({hotel.photoCategories.amenities.length})
                      </button>
                    </div>
                  </div>
                  
                  {/* Photo Grid */}
                  <div className="photo-grid">
                    <div className="row g-3">
                      {hotel.photoCategories[selectedPhotoCategory].map((image, index) => (
                        <div key={index} className="col-6 col-md-4">
                          <div 
                            className="photo-card"
                            onClick={() => {
                              // Find index in all photos to set correct currentImageIndex
                              const allIndex = hotel.images.findIndex(img => img === image);
                              if (allIndex !== -1) {
                                setCurrentImageIndex(allIndex);
                              }
                              // Scroll back to gallery
                              window.scrollTo(0, document.querySelector('.image-gallery-container').offsetTop - 100);}}
                              >
                                <img 
                                  src={image} 
                                  alt={`${hotel.name} view ${index + 1}`}
                                  className="img-fluid rounded"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://via.placeholder.com/300x200?text=Hotel+Photo";
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Rooms Tab */}
                  {activeTab === 'rooms' && (
                    <div className="rooms-tab">
                      {hotel.rooms.map(room => (
                        <div key={room.id} className="room-card card mb-4">
                          <div className="row g-0">
                            <div className="col-md-4">
                              <img 
                                src={room.image}
                                alt={room.name}
                                className="room-image"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "https://via.placeholder.com/300x200?text=Room+Image";
                                }}
                              />
                            </div>
                            <div className="col-md-8">
                              <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start flex-wrap">
                                  <h3 className="card-title h5">{room.name}</h3>
                                  <div className="price-info mt-2 mt-sm-0">
                                    <small className="text-muted">Per night</small>
                                    <div className="price">${room.price}</div>
                                  </div>
                                </div>
                                <div className="room-details mb-3">
                                  <span className="me-3"><i className="bi bi-rulers"></i> {room.size}</span>
                                  <span className="me-3"><i className="bi bi-person"></i> {room.occupancy}</span>
                                  <span><i className="bi bi-calendar"></i> {room.beds}</span>
                                </div>
                                <div className="room-amenities mb-3">
                                  {room.amenities.map((amenity, index) => (
                                    <span key={index} className="badge bg-light text-dark me-1 mb-1">
                                      {amenity}
                                    </span>
                                  ))}
                                </div>
                                <div className="d-flex justify-content-end">
                                  <button className="btn btn-outline-primary me-2">
                                    Details
                                  </button>
                                  <button className="btn btn-primary">
                                    Select
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Amenities Tab */}
                  {activeTab === 'amenities' && (
                    <div className="amenities-tab">
                      <div className="row">
                        <div className="col-md-6 mb-4">
                          <div className="card h-100">
                            <div className="card-body">
                              <h3 className="card-title h5 mb-3">
                                <Wifi size={18} className="me-2" />
                                Basic Amenities
                              </h3>
                              <ul className="amenities-list">
                                {hotel.amenitiesDetails.basic.map((amenity, index) => (
                                  <li key={index}>
                                    <Check size={16} className="text-success me-2" />
                                    {amenity}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 mb-4">
                          <div className="card h-100">
                            <div className="card-body">
                              <h3 className="card-title h5 mb-3">
                                <Coffee size={18} className="me-2" />
                                Food & Drink
                              </h3>
                              <ul className="amenities-list">
                                {hotel.amenitiesDetails.food.map((amenity, index) => (
                                  <li key={index}>
                                    <Check size={16} className="text-success me-2" />
                                    {amenity}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 mb-4">
                          <div className="card h-100">
                            <div className="card-body">
                              <h3 className="card-title h5 mb-3">
                                <i className="bi bi-droplet me-2"></i>
                                Bathroom
                              </h3>
                              <ul className="amenities-list">
                                {hotel.amenitiesDetails.bathroom.map((amenity, index) => (
                                  <li key={index}>
                                    <Check size={16} className="text-success me-2" />
                                    {amenity}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 mb-4">
                          <div className="card h-100">
                            <div className="card-body">
                              <h3 className="card-title h5 mb-3">
                                <i className="bi bi-person-check me-2"></i>
                                Services
                              </h3>
                              <ul className="amenities-list">
                                {hotel.amenitiesDetails.services.map((amenity, index) => (
                                  <li key={index}>
                                    <Check size={16} className="text-success me-2" />
                                    {amenity}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Reviews Tab - Modern Design */}
                  {activeTab === 'reviews' && (
                    <div className="reviews-tab">
                      {/* Reviews Summary Card */}
                      <div className="card mb-4 shadow-sm">
                        <div className="card-body">
                          <div className="row align-items-center">
                            <div className="col-md-4 text-center mb-4 mb-md-0">
                              <div className="overall-rating">
                                <span className="rating-large">{hotel.reviews.average}</span>
                                <div className="d-flex justify-content-center my-2">
                                  {[...Array(5)].map((_, i) => (
                                    <Star 
                                      key={i} 
                                      size={24} 
                                      className="text-warning" 
                                      fill={i < Math.round(hotel.reviews.average) ? "#ffc107" : "none"} 
                                    />
                                  ))}
                                </div>
                                <p className="text-muted">Based on {hotel.reviews.count} reviews</p>
                              </div>
                            </div>
                            <div className="col-md-8">
                              <div className="rating-categories">
                                <div className="rating-category mb-3">
                                  <div className="d-flex justify-content-between mb-1">
                                    <span className="fw-medium">Location</span>
                                    <span className="fw-medium">{hotel.reviews.categories.location}</span>
                                  </div>
                                  <div className="progress" style={{height: "8px"}}>
                                    <div 
                                      className={`progress-bar ${getCategoryColor(hotel.reviews.categories.location)}`}
                                      style={{width: `${(hotel.reviews.categories.location / 5) * 100}%`}}
                                    ></div>
                                  </div>
                                </div>
                                <div className="rating-category mb-3">
                                  <div className="d-flex justify-content-between mb-1">
                                    <span className="fw-medium">Cleanliness</span>
                                    <span className="fw-medium">{hotel.reviews.categories.cleanliness}</span>
                                  </div>
                                  <div className="progress" style={{height: "8px"}}>
                                    <div 
                                      className={`progress-bar ${getCategoryColor(hotel.reviews.categories.cleanliness)}`}
                                      style={{width: `${(hotel.reviews.categories.cleanliness / 5) * 100}%`}}
                                    ></div>
                                  </div>
                                </div>
                                <div className="rating-category mb-3">
                                  <div className="d-flex justify-content-between mb-1">
                                    <span className="fw-medium">Service</span>
                                    <span className="fw-medium">{hotel.reviews.categories.service}</span>
                                  </div>
                                  <div className="progress" style={{height: "8px"}}>
                                    <div 
                                      className={`progress-bar ${getCategoryColor(hotel.reviews.categories.service)}`}
                                      style={{width: `${(hotel.reviews.categories.service / 5) * 100}%`}}
                                    ></div>
                                  </div>
                                </div>
                                <div className="rating-category">
                                  <div className="d-flex justify-content-between mb-1">
                                    <span className="fw-medium">Value</span>
                                    <span className="fw-medium">{hotel.reviews.categories.value}</span>
                                  </div>
                                  <div className="progress" style={{height: "8px"}}>
                                    <div 
                                      className={`progress-bar ${getCategoryColor(hotel.reviews.categories.value)}`}
                                      style={{width: `${(hotel.reviews.categories.value / 5) * 100}%`}}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <h3 className="h5 mb-4 fw-bold">Guest Reviews</h3>
                      
                      {/* Modern Review Cards */}
                      <div className="modern-reviews">
                        {hotel.reviews.items.map((review) => (
                          <div 
                            key={review.id} 
                            className="card mb-4 shadow-sm rounded-lg overflow-hidden transition-all duration-300 hover:shadow"
                          >
                            <div className="card-body p-4">
                              <div className="d-flex">
                                {/* Avatar and Name */}
                                <div className="me-3">
                                  <div className="position-relative">
                                    <img 
                                      src={review.avatar || `/api/placeholder/50/50?text=${review.name.charAt(0)}`} 
                                      alt={review.name} 
                                      className="rounded-circle border border-2 border-light" 
                                      width="48" 
                                      height="48"
                                    />
                                    <div className="position-absolute bottom-0 end-0 bg-white rounded-circle p-1" style={{marginRight: "-3px", marginBottom: "-3px"}}>
                                      <div className="bg-success rounded-circle" style={{width: "10px", height: "10px"}}></div>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Review Header */}
                                <div className="flex-grow-1">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <h4 className="h6 fw-bold mb-1">{review.name}</h4>
                                      <div className="text-muted small">{review.date}</div>
                                    </div>
                                    <div className="d-flex">
                                      {renderStars(review.rating)}
                                    </div>
                                  </div>
                                  
                                  {/* Review Content */}
                                  <div className="mt-3">
                                    <p className="mb-0 text-secondary">{review.comment}</p>
                                  </div>
                                  
                                  {/* Review Actions */}
                                  <div className="mt-3 d-flex justify-content-between align-items-center">
                                    <div className="d-flex">
                                      <button className="btn btn-sm btn-link text-decoration-none text-secondary me-3 p-0">
                                        <ThumbsUp size={16} className="me-1" />
                                        <span className="small">Helpful ({review.helpful || 0})</span>
                                      </button>
                                      <button className="btn btn-sm btn-link text-decoration-none text-secondary me-3 p-0">
                                        <MessageSquare size={16} className="me-1" />
                                        <span className="small">Reply ({review.replies || 0})</span>
                                      </button>
                                      <button className="btn btn-sm btn-link text-decoration-none text-secondary p-0">
                                        <Flag size={16} className="me-1" />
                                        <span className="small">Report</span>
                                      </button>
                                    </div>
                                    
                                    <button 
                                      className="btn btn-sm btn-link text-decoration-none text-primary p-0"
                                      onClick={() => toggleExpandReview(review.id)}
                                    >
                                      <span className="small fw-medium me-1">
                                        {expandedReview === review.id ? "Hide details" : "Show details"}
                                      </span>
                                      {expandedReview === review.id ? (
                                        <ChevronUp size={16} />
                                      ) : (
                                        <ChevronDown size={16} />
                                      )}
                                    </button>
                                  </div>
                                  
                                  {/* Expanded Details */}
                                  {expandedReview === review.id && (
                                    <div className="mt-3 pt-3 border-top">
                                      <div className="row">
                                        <div className="col-md-6 mb-3">
                                          <h5 className="h6 fw-medium text-secondary mb-2">Location</h5>
                                          <div className="d-flex align-items-center">
                                            <div className="flex-grow-1 bg-light rounded me-2" style={{height: "8px"}}>
                                              <div 
                                                className={`${getCategoryColor(review.categories?.location || 4)}`}
                                                style={{
                                                  width: `${((review.categories?.location || 4) / 5) * 100}%`,
                                                  height: "8px",
                                                  borderRadius: "4px"
                                                }}
                                              ></div>
                                            </div>
                                            <span className="fw-medium">{review.categories?.location || 4}</span>
                                          </div>
                                        </div>
                                        
                                        <div className="col-md-6 mb-3">
                                          <h5 className="h6 fw-medium text-secondary mb-2">Cleanliness</h5>
                                          <div className="d-flex align-items-center">
                                            <div className="flex-grow-1 bg-light rounded me-2" style={{height: "8px"}}>
                                              <div 
                                                className={`${getCategoryColor(review.categories?.cleanliness || 4)}`}
                                                style={{
                                                  width: `${((review.categories?.cleanliness || 4) / 5) * 100}%`,
                                                  height: "8px",
                                                  borderRadius: "4px"
                                                }}
                                              ></div>
                                            </div>
                                            <span className="fw-medium">{review.categories?.cleanliness || 4}</span>
                                          </div>
                                        </div>
                                        
                                        <div className="col-md-6 mb-3">
                                          <h5 className="h6 fw-medium text-secondary mb-2">Service</h5>
                                          <div className="d-flex align-items-center">
                                            <div className="flex-grow-1 bg-light rounded me-2" style={{height: "8px"}}>
                                              <div 
                                                className={`${getCategoryColor(review.categories?.service || 4)}`}
                                                style={{
                                                  width: `${((review.categories?.service || 4) / 5) * 100}%`,
                                                  height: "8px",
                                                  borderRadius: "4px"
                                                }}
                                              ></div>
                                            </div>
                                            <span className="fw-medium">{review.categories?.service || 4}</span>
                                          </div>
                                        </div>
                                        
                                        <div className="col-md-6 mb-3">
                                          <h5 className="h6 fw-medium text-secondary mb-2">Value</h5>
                                          <div className="d-flex align-items-center">
                                            <div className="flex-grow-1 bg-light rounded me-2" style={{height: "8px"}}>
                                              <div 
                                                className={`${getCategoryColor(review.categories?.value || 4)}`}
                                                style={{
                                                  width: `${((review.categories?.value || 4) / 5) * 100}%`,
                                                  height: "8px",
                                                  borderRadius: "4px"
                                                }}
                                              ></div>
                                            </div>
                                            <span className="fw-medium">{review.categories?.value || 4}</span>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      <div className="bg-light p-3 rounded mt-2">
                                        <h5 className="h6 fw-medium mb-2">Stay details</h5>
                                        <div className="row">
                                          <div className="col-md-6 mb-2">
                                            <span className="text-secondary">Stay date:</span> 
                                            <span className="ms-1 fw-medium">{review.date}</span>
                                          </div>
                                          <div className="col-md-6 mb-2">
                                            <span className="text-secondary">Room type:</span>
                                            <span className="ms-1 fw-medium">Deluxe King</span>
                                          </div>
                                          <div className="col-md-6 mb-md-0 mb-2">
                                            <span className="text-secondary">Stay duration:</span>
                                            <span className="ms-1 fw-medium">3 nights</span>
                                          </div>
                                          <div className="col-md-6">
                                            <span className="text-secondary">Trip type:</span>
                                            <span className="ms-1 fw-medium">Business</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="text-center mt-4">
                        <button className="btn btn-outline-primary px-4">
                          Load more reviews
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Policies Tab */}
                  {activeTab === 'policies' && (
                    <div className="policies-tab">
                      <div className="row">
                        <div className="col-md-6 mb-4">
                          <div className="card h-100">
                            <div className="card-body">
                              <h3 className="card-title h5 mb-3">
                                <Clock size={18} className="me-2" />
                                Check-in/Check-out
                              </h3>
                              <ul className="policy-list">
                                <li className="mb-2">
                                  <strong>Check-in:</strong> {hotel.policies.checkIn}
                                </li>
                                <li>
                                  <strong>Check-out:</strong> {hotel.policies.checkOut}
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 mb-4">
                          <div className="card h-100">
                            <div className="card-body">
                              <h3 className="card-title h5 mb-3">
                                <i className="bi bi-cash-coin me-2"></i>
                                Cancellation
                              </h3>
                              <p>{hotel.policies.cancellation}</p>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 mb-4">
                          <div className="card h-100">
                            <div className="card-body">
                              <h3 className="card-title h5 mb-3">
                                <Users size={18} className="me-2" />
                                Children Policy
                              </h3>
                              <p>{hotel.policies.children}</p>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 mb-4">
                          <div className="card h-100">
                            <div className="card-body">
                              <h3 className="card-title h5 mb-3">
                                <i className="bi bi-credit-card me-2"></i>
                                Payment
                              </h3>
                              <p>{hotel.policies.payment}</p>
                              <p className="mb-0">{hotel.policies.pets}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Booking Sidebar - with fullscreen on mobile support */}
              <div className={`col-lg-4 ${isMobile && fullScreenBooking ? 'booking-fullscreen' : ''}`}>
                {isMobile && fullScreenBooking && (
                  <div className="fullscreen-close">
                    <button
                      className="btn btn-light rounded-circle"
                      onClick={toggleFullScreenBooking}
                    >
                      <ChevronDown size={24} />
                    </button>
                  </div>
                )}
                
                <div className={`booking-card card ${isMobile ? 'mobile-booking' : 'sticky-top'}`}>
                  <div className="card-body">
                    <h3 className="card-title h5 mb-3">Book Your Stay</h3>
                    
                    <div className="price-details mb-3">
                      <div className="current-price h4 mb-1">${hotel.price} <small className="text-muted">/ night</small></div>
                      {hotel.discount && (
                        <div className="discount">
                          <span className="badge bg-danger me-2">{hotel.discount}</span>
                          <span className="original-price text-decoration-line-through text-muted">
                            ${Math.round(hotel.price * 1.2)}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <form>
                      <div className="mb-3">
                        <label className="form-label d-flex align-items-center">
                          <Calendar size={16} className="me-2" />
                          Check-in Date
                        </label>
                        <input 
                          type="date" 
                          className="form-control"
                          value={bookingInfo.checkIn}
                          onChange={(e) => setBookingInfo(prev => ({...prev, checkIn: e.target.value}))}
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label d-flex align-items-center">
                          <Calendar size={16} className="me-2" />
                          Check-out Date
                        </label>
                        <input 
                          type="date" 
                          className="form-control"
                          value={bookingInfo.checkOut}
                          onChange={(e) => setBookingInfo(prev => ({...prev, checkOut: e.target.value}))}
                          min={bookingInfo.checkIn || new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      
                      <div className="mb-4">
                        <label className="form-label d-flex align-items-center">
                          <Users size={16} className="me-2" />
                          Guests
                        </label>
                        <div className="guest-picker position-relative">
                          <div 
                            className="form-control d-flex justify-content-between align-items-center"
                            onClick={() => setShowGuestPicker(!showGuestPicker)}
                            style={{cursor: 'pointer'}}
                          >
                            <span>
                              {`${bookingInfo.guests.adults} Adults, ${bookingInfo.guests.children} Children, ${bookingInfo.guests.rooms} Room${bookingInfo.guests.rooms > 1 ? 's' : ''}`}
                            </span>
                            <i className={`bi bi-chevron-${showGuestPicker ? 'up' : 'down'}`}></i>
                          </div>
                          
                          {showGuestPicker && (
                            <div className="guest-dropdown">
                              <div className="guest-type">
                                <label>Adults</label>
                                <div className="guest-controls">
                                  <button 
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => handleGuestChange('adults', 'decrement')}
                                    disabled={bookingInfo.guests.adults <= 1}
                                  >-</button>
                                  <span>{bookingInfo.guests.adults}</span>
                                  <button 
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => handleGuestChange('adults', 'increment')}
                                  >+</button>
                                </div>
                              </div>
    
                              <div className="guest-type">
                                <label>Children</label>
                                <div className="guest-controls">
                                  <button 
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => handleGuestChange('children', 'decrement')}
                                    disabled={bookingInfo.guests.children <= 0}
                                  >-</button>
                                  <span>{bookingInfo.guests.children}</span>
                                  <button 
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => handleGuestChange('children', 'increment')}
                                  >+</button>
                                </div>
                              </div>
    
                              <div className="guest-type">
                                <label>Rooms</label>
                                <div className="guest-controls">
                                  <button 
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => handleGuestChange('rooms', 'decrement')}
                                    disabled={bookingInfo.guests.rooms <= 1}
                                  >-</button>
                                  <span>{bookingInfo.guests.rooms}</span>
                                  <button 
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => handleGuestChange('rooms', 'increment')}
                                  >+</button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {nights > 0 && (
                        <div className="price-calculation mb-3">
                          <div className="d-flex justify-content-between mb-2">
                            <span>${hotel.price} × {nights} nights</span>
                            <span>${hotel.price * nights}</span>
                          </div>
                          {bookingInfo.guests.rooms > 1 && (
                            <div className="d-flex justify-content-between mb-2">
                              <span>{bookingInfo.guests.rooms} rooms</span>
                              <span>× {bookingInfo.guests.rooms}</span>
                            </div>
                          )}
                          <hr />
                          <div className="d-flex justify-content-between fw-bold">
                            <span>Total</span>
                            <span>${totalPrice}</span>
                          </div>
                        </div>
                      )}
                      
                      <button 
                        type="submit" 
                        className="btn btn-primary w-100"
                        disabled={!bookingInfo.checkIn || !bookingInfo.checkOut}
                      >
                        Reserve Now
                      </button>
                      
                      <div className="text-center mt-3">
                        <small className="text-muted">You won't be charged yet</small>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Similar Hotels */}
          <div className="similar-hotels-section py-5 bg-light">
            <div className="container">
              <h2 className="section-title mb-4">You Might Also Like</h2>
              <div className="row g-4">
                {hotelData.hotels
                  .filter(h => h.id !== hotel.id)
                  .slice(0, 3)
                  .map(similarHotel => (
                    <div key={similarHotel.id} className="col-md-4">
                      <div className="card hotel-card h-100">
                        <img 
                          src={similarHotel.image}
                          alt={similarHotel.name}
                          className="card-img-top"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/300x200?text=Hotel+Image";
                          }}
                        />
                        <div className="card-body">
                          <h5 className="card-title">{similarHotel.name}</h5>
                          <p className="card-text mb-1">
                            <MapPin size={16} className="me-1" />
                            {`${similarHotel.location.city}, ${similarHotel.location.country}`}
                          </p>
                          <div className="mb-2">
                            {[...Array(similarHotel.stars)].map((_, i) => (
                              <Star 
                              key={i} 
                              size={14} 
                              className="text-warning" 
                              fill="#ffc107" 
                            />
                          ))}
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">Per night</small>
                            <div className="fw-bold">${similarHotel.price}</div>
                          </div>
                          <Link 
                            to={`/hotel/${similarHotel.id}`}
                            className="btn btn-outline-primary btn-sm"
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  export default HotelDetails;
                                